import { useState } from 'react';
import { FormNotifications, RegisterUserStep } from 'types';
import { acceptAgreement, registerUser } from 'api';
import { formatErrorsForFormNotification } from 'utils/services';
import { safeTextInsert } from 'utils/formatters';
import { DynamicForm } from 'components/DynamicForm';
import { SignUpWrapper } from 'containers/SignWrapper';
import { getSignUpInitialFields } from './Steps';
import { getAgreementFields } from './Steps/agreementFields';
import { CommentedLink } from 'components/CommentedLink/CommentedLink';

import styles from '../SignInPage/SignIn.module.scss';
import cx from 'classnames';
import { PRPostsReviews } from '../ProductPage/PRPostsReviews';
import { FaqContent } from '../FAQ/FAQ';
import { PromoBlocks } from './PromoBlocks';
import Image from 'next/image';
import { NextLink } from '../../components/NextLink';
import { APPSTORE_APP_LINK, GOOGLE_PLAY_APP_LINK } from '../../consts';

export const SignUpTitlePage = ({ signInSlides, currentUser, email, enterStepper, setCurrentUser, query, externalReviews, prPosts, faqTopics }: any) => {
  const [passToAgreement, setPassToAgreement] = useState(currentUser?.email && !currentUser?.tac_accepted_at);
  const [notifications, setNotifications] = useState<Record<any, FormNotifications>>();

  const signUp = async (data: RegisterUserStep) => {
    if (!email && currentUser?.email === data?.email) {
      currentUser?.tac_accepted_at ? enterStepper() : setPassToAgreement(true);
    } else {
      const response = await registerUser(email ? { ...data, email: email.toLowerCase() } : data);
      if (response.ok) {
        setCurrentUser(response.data);
        setPassToAgreement(!response.data?.tac_accepted_at);
      } else {
        setNotifications(formatErrorsForFormNotification(response));
      }
    }
  };

  const onAgree = async () => {
    if (currentUser?.tac_accepted_at) {
      enterStepper();
    }
    const response = await acceptAgreement();
    if (response.ok) {
      setCurrentUser(response.data);
      setPassToAgreement(false);
    } else {
      setNotifications(formatErrorsForFormNotification(response));
    }
  };

  const onDecline = () => {
    setPassToAgreement(false);
  };

  return (
    <SignUpWrapper
      slides={signInSlides}
      footer={
        passToAgreement || (<>
          <PRPostsReviews posts={prPosts?.data || []} reviews={externalReviews?.data || []} hideVideo hideDivider />

          <div className={cx(styles.certifiedImage, styles.forMob)}>
            <Image alt="CertifiedLogo" src="CertifiedLogo.svg" layout="fill" objectFit="contain" />
          </div>

          <div className={styles.stepsWrapper}>
            <PromoBlocks steps={buySteps} image={'/PromoBuy1.png'} />
            <PromoBlocks steps={sellSteps} image={'/PromoSell.png'} />
            <PromoBlocks steps={rentSteps} image={'/PromoRent.png'} />
          </div>

          <div className={styles.moreDetailsContainer}>
            <div className={styles.moreDetailsTitle}>MORE DETAILS</div>
            <div className={styles.moreDetailsDescription}>When you Rent or Sell an item, your funds will be transferred to your SENDY wallet. The funds in the wallet can be used on SENDY, or be transferred to your account.</div>
          </div>

          <DownloadBannerNew />

          <FaqContent faqTopics={faqTopics}/>
        </>)
      }
    >
      <div className={styles.container}>
        <div className={styles.contentWrapper}>
          {passToAgreement ? (
            <DynamicForm
              buttonCaption="Agree"
              cancelCaption="Decline"
              fields={getAgreementFields()}
              marginMode="none"
              notifications={notifications}
              onCancel={onDecline}
              onSend={onAgree}
              verticalButtons={true}
              buttonProps={{ isBoldText: true }}
            />
          ) : (
            <>
              <div className={cx(styles.promoBox, styles.forMob)} onClick={() => window.location.assign('https://onelink.to/sendy')}>
                <video className={styles.promo} playsInline autoPlay muted loop>
                  <source src="/promoMob.mp4" type="video/mp4" />
                </video>
              </div>
              <DynamicForm
                buttonCaption="Continue"
                fields={getSignUpInitialFields(currentUser, safeTextInsert(query?.redirect), email)}
                marginMode="none"
                notifications={notifications}
                onSend={signUp}
                buttonProps={{ isBoldText: true }}
              />
              <div className={styles.mobileLinks}>
                <CommentedLink
                  caption="Already have an account?"
                  linkId="title-signup-page-sign-in"
                  linkHref="/sign-in"
                  linkCaption="Sign In"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </SignUpWrapper>
  );
};

export const DownloadBannerNew = () => (
  <>
    <div className={cx(styles.promoRow, styles.forNotMob)}>
      <div>
        <div className={styles.downloadTitle}>Download the App<br/>on iPhone and Android.</div>
        <div className={styles.downloadDescription}>Make your orders and sales faster with our crossplatform application</div>
        <div className={styles.downloadBannerIcons}>
          <NextLink href={APPSTORE_APP_LINK} newTab={true}>
            <Image
              alt="App Store"
              src="/App_Store_Badge.svg"
              width={144}
              height={48}
              layout="fixed"
              objectFit="cover"
            />
          </NextLink>
          <NextLink href={GOOGLE_PLAY_APP_LINK} newTab={true}>
            <Image
              alt="Google Pay"
              src="/Google_Play_Store.svg"
              width={162}
              height={48}
              layout="fixed"
              objectFit="cover"
            />
          </NextLink>
        </div>
      </div>
      <div className={styles.downloadImage}>
        <Image alt="TwoPhonesPromo" src="/TwoPhonesPromo.png" layout="fill" objectFit="contain" />
      </div>
    </div>
    <div className={cx(styles.mobPromoRow, styles.forMob)}>
      <NextLink href={'/blog'}>
        <div className={styles.downloadImage}>
          <Image alt="TwoPhonesPromo" src="/TwoPhonesPromo.png" layout="fill" objectFit="contain" />
        </div>
      </NextLink>
      <div className={styles.downloadTitle}>Download the App<br/>on iPhone and Android</div>
      <div className={styles.downloadDescription}>Make your orders and sales faster with our<br/>crossplatform application</div>
      <div className={styles.downloadBannerIcons}>
        <NextLink href={APPSTORE_APP_LINK} newTab={true}>
          <Image
            alt="App Store"
            src="/App_Store_Badge.svg"
            width={144}
            height={48}
            layout="fixed"
            objectFit="cover"
          />
        </NextLink>
        <NextLink href={GOOGLE_PLAY_APP_LINK} newTab={true}>
          <Image
            alt="Google Pay"
            src="/Google_Play_Store.svg"
            width={162}
            height={48}
            layout="fixed"
            objectFit="cover"
          />
        </NextLink>
      </div>
    </div>
  </>
)


const buySteps = [
  'Buy or make an offer on gear',
  'Seller ships gear to you via UPS',
  'Receive the gear and approve it',
  'Leave a review of the Seller',
]

const sellSteps = [
  'List your gear for sale',
  'Sell for full price, accept an offer, or make a counteroffer',
  'Box up the gear and SEND IT with a pre-paid shipping label provided by SENDY',
  '24 hours after the gear is delivered, your funds are released',
]

const rentSteps = [
  'Find the gear you need, where you need it, when you need it',
  'Select day, time, hours. Checkout',
  'Agree on meet up or pick up location',
  'Return the gear',
]
